import type {
  ContributionFileKind,
  CreateContributionInput,
  HistoryContributionPayload,
  HistoryContributionStatus,
  MergePreview,
  MergeSummary,
  ReviewContributionInput,
} from '@gwc/shared';
import { api, uploadWithProgress } from '../lib/api';

export interface ContributionListItem {
  id: string;
  status: HistoryContributionStatus;
  title: string | null;
  year: number | null;
  linkedEventId: string | null;
  mergedEventId: string | null;
  submittedBy: string;
  submitterName: string;
  submittedAt: string | null;
  reviewNotes: string | null;
  fileCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ContributionFileRow {
  id: string;
  kind: ContributionFileKind;
  filename: string;
  contentType: string;
  sizeBytes: number;
  caption: string | null;
  sortOrder: number;
  url: string;
  promoted: boolean;
  createdAt: string;
}

export interface ContributionRow {
  id: string;
  status: HistoryContributionStatus;
  payload: HistoryContributionPayload;
  linkedEventId: string | null;
  submittedBy: string;
  submittedAt: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
  internalNotes?: string | null;
  mergedEventId: string | null;
  mergeSummary: MergeSummary | null;
  createdAt: string;
  updatedAt: string;
}

export interface EventSummaryRow {
  id: string;
  title: string;
  eventYear: number;
  eventMonth: number | null;
  eventDate: string | null;
  datePrecision: string;
  status: string;
}

export interface ContributionDetail {
  contribution: ContributionRow;
  files: ContributionFileRow[];
  submitter: { id: string; firstName: string; lastName: string } | null;
  linkedEvent: EventSummaryRow | null;
  mergedEvent: EventSummaryRow | null;
}

/** What the reviewer sends to preview/merge — the schema's INPUT shape (defaults optional). */
export interface MergeRequest {
  title: string;
  eventYear: number;
  eventMonth?: number | null;
  eventDay?: number | null;
  useLinkedEvent?: boolean;
  letters?: Record<string, string>;
  personMap?: Record<string, string>;
  createUnknownPeople?: boolean;
  overwriteScores?: boolean;
  overwriteEventFields?: boolean;
}

const BASE = '/history-contributions';

export const historyApi = {
  list: (params: { scope?: 'mine' | 'all'; status?: HistoryContributionStatus; page?: number } = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params)
        .filter(([, v]) => v != null)
        .map(([k, v]) => [k, String(v)]),
    ).toString();
    return api.get<{ items: ContributionListItem[]; total: number; page: number; pageSize: number }>(
      `${BASE}${qs ? `?${qs}` : ''}`,
    );
  },
  get: (id: string) => api.get<ContributionDetail>(`${BASE}/${id}`),
  create: (body: CreateContributionInput = {}) =>
    api.post<{ contribution: ContributionRow }>(BASE, body).then((r) => r.contribution),
  update: (id: string, payload: HistoryContributionPayload) =>
    api.patch<{ contribution: ContributionRow }>(`${BASE}/${id}`, { payload }).then((r) => r.contribution),
  submit: (id: string) => api.post<{ contribution: ContributionRow }>(`${BASE}/${id}/submit`),
  withdraw: (id: string) => api.post<{ contribution: ContributionRow }>(`${BASE}/${id}/withdraw`),
  remove: (id: string) => api.del<void>(`${BASE}/${id}`),

  uploadFile: (
    id: string,
    file: File,
    opts: { kind?: ContributionFileKind; caption?: string; onProgress?: (f: number) => void } = {},
  ) => {
    const form = new FormData();
    // Text fields first: the server reads the ones that arrive before the file part.
    if (opts.kind) form.append('kind', opts.kind);
    if (opts.caption) form.append('caption', opts.caption);
    form.append('file', file);
    return uploadWithProgress<{ file: ContributionFileRow }>(`${BASE}/${id}/files`, form, opts.onProgress).then(
      (r) => r.file,
    );
  },
  patchFile: (id: string, fileId: string, patch: { kind?: ContributionFileKind; caption?: string | null; sortOrder?: number }) =>
    api.patch<{ file: ContributionFileRow }>(`${BASE}/${id}/files/${fileId}`, patch).then((r) => r.file),
  deleteFile: (id: string, fileId: string) => api.del<void>(`${BASE}/${id}/files/${fileId}`),
  /** Authenticated bytes for a private contribution file (the URL is not public). */
  fileBlob: (url: string) => api.blob(url.replace(/^\/api\/v\d+/, '')),

  review: (id: string, body: ReviewContributionInput) =>
    api.post<{ contribution: ContributionRow }>(`${BASE}/${id}/review`, body).then((r) => r.contribution),
  mergePreview: (id: string, body: MergeRequest) =>
    api.post<{ preview: MergePreview }>(`${BASE}/${id}/merge-preview`, body).then((r) => r.preview),
  merge: (id: string, body: MergeRequest) =>
    api.post<{ contribution: ContributionRow; summary: MergePreview }>(`${BASE}/${id}/merge`, body),
  eventFiles: (eventId: string) =>
    api.get<{ files: ContributionFileRow[] }>(`${BASE}/for-event/${eventId}/files`).then((r) => r.files),
};
